import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Map, Target, Send, BarChart, CheckCircle2 } from 'lucide-react';
export function PlanRegionalTourPage() {
  const steps = [{
    title: 'Define the run',
    desc: 'Set region, timing, price point, and routing constraints.',
    icon: Map
  }, {
    title: 'Generate route options',
    desc: 'See safe, balanced, and upside routes with travel and gap warnings.',
    icon: Target
  }, {
    title: 'Find venues that fit',
    desc: 'Filter by capacity, genre, geography, and contact quality.',
    icon: CheckCircle2
  }, {
    title: 'Launch outreach',
    desc: 'Use templates with AI personalization to start contacting venues fast.',
    icon: Send
  }, {
    title: 'Track the results',
    desc: 'See which cities, venues, and messages are actually moving the route forward.',
    icon: BarChart
  }];
  return <div className="min-h-screen pt-32 pb-24" data-id="element-7351">
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 mb-20 text-center" data-id="element-7352">
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-4xl md:text-5xl font-bold text-textMain mb-6 tracking-tight" data-id="element-7353">
          Turn a vague tour idea into a real route with real venues.
        </motion.h1>
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="text-lg text-textMuted leading-relaxed" data-id="element-7354">
          Set your dates, region, genre, pricing, and routing rules. Booker maps
          the run, finds likely venues, drafts outreach, and helps you turn a
          plan into booked shows.
        </motion.p>
      </section>

      {/* Problem Section */}
      <section className="max-w-4xl mx-auto px-6 mb-20" data-id="element-7355">
        <div className="bg-red-500/5 border border-red-500/10 rounded-2xl p-8 md:p-12 text-center" data-id="element-7356">
          <h2 className="text-2xl font-bold text-textMain mb-4" data-id="element-7357">
            Planning a run usually starts with guesswork.
          </h2>
          <p className="text-textMuted leading-relaxed" data-id="element-7358">
            Artists often piece together tours from memory, Google searches,
            spreadsheets, and scattered contacts. That makes routing weaker,
            venue targeting slower, and outreach harder to launch.
          </p>
        </div>
      </section>

      {/* Scenario Story */}
      <section className="max-w-4xl mx-auto px-6 mb-24" data-id="element-7359">
        <div className="bg-surface border border-border rounded-2xl p-8 md:p-12 relative overflow-hidden" data-id="element-7360">
          <div className="absolute top-0 left-0 w-1 h-full bg-primary" data-id="element-7361"></div>
          <div className="prose prose-invert max-w-none" data-id="element-7362">
            <p className="italic text-lg text-textMuted leading-relaxed mb-6" data-id="element-7363">
              Mark wants to take his folk/bluegrass band on the road in June. He
              knows he wants to start in Michigan and work up the New England
              coast, but he does not want to spend two weeks digging up venues,
              guessing routing, and juggling spreadsheets.
            </p>
            <p className="italic text-lg text-textMuted leading-relaxed" data-id="element-7364">
              With Booker, he sets the date range, target region, room size, and
              fee expectation. Booker generates route options, finds matching
              venues along the run, and prepares the first round of outreach so
              the campaign starts with momentum instead of guesswork.
            </p>
          </div>
        </div>
      </section>

      {/* Workflow Steps */}
      <section className="max-w-4xl mx-auto px-6 mb-24" data-id="element-7365">
        <h2 className="text-3xl font-bold text-textMain mb-12 text-center" data-id="element-7366">
          How Booker turns a route idea into a campaign
        </h2>
        <div className="space-y-6" data-id="element-7367">
          {steps.map((step, i) => <motion.div key={i} initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="flex items-start gap-6 bg-surface border border-border rounded-2xl p-6" data-id="element-7368">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0" data-id="element-7369">
                <step.icon className="w-6 h-6 text-primary" data-id="element-7370" />
              </div>
              <div data-id="element-7371">
                <h3 className="text-xl font-bold text-textMain mb-2" data-id="element-7372">
                  Step {i + 1} — {step.title}
                </h3>
                <p className="text-textMuted" data-id="element-7373">{step.desc}</p>
              </div>
            </motion.div>)}
        </div>
      </section>

      {/* Outcomes */}
      <section className="max-w-4xl mx-auto px-6 mb-24" data-id="element-7374">
        <div className="bg-surface border border-border rounded-2xl p-8 md:p-12" data-id="element-7375">
          <h2 className="text-2xl font-bold text-textMain mb-8" data-id="element-7376">
            Spend less time planning. Spend more time getting booked.
          </h2>
          <ul className="space-y-4" data-id="element-7377">
            {['clearer route decisions', 'stronger venue targeting', 'faster outreach launch', 'fewer manual tools', 'better visibility into what still needs to be filled'].map((item, i) => <li key={i} className="flex items-center gap-3 text-textMuted" data-id="element-7378">
                <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" data-id="element-7379" />
                <span className="text-lg" data-id="element-7380">{item}</span>
              </li>)}
          </ul>
        </div>
      </section>

      {/* CTA Band */}
      <section className="max-w-4xl mx-auto px-6 text-center" data-id="element-7381">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-7382">
          <Link to="/onboarding" className="px-8 py-3 bg-primary text-background font-bold rounded-lg hover:bg-primaryHover transition-colors" data-id="element-7383">
            Start free
          </Link>
          <Link to="/demo" className="px-8 py-3 bg-surface border border-border text-textMain font-medium rounded-lg hover:bg-surfaceHover transition-colors" data-id="element-7384">
            See Booker in action
          </Link>
        </div>
      </section>
    </div>;
}